import { useState } from 'react';
import { Handshake, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Button, Skeleton } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import {
  useJourneyNegotiations,
  type CustomerJourney,
} from '@/api/customer-journey';
import { TabError } from '@/pages/customers/detail/tab-states';
import { isWonStep } from './sale-process-helpers';
import { NegotiationRow } from './negotiation-row';
import { CreateNegotiationDialog } from './create-negotiation-dialog';
import { EntityLink } from '@/components/entity-link';

/**
 * Deal Negotiation panel — the journey→contract flow. Lists the journey's
 * negotiations (each expandable to its history with Resolve / Reject / Convert),
 * lets a user create a negotiation (full form, matching legacy), and surfaces
 * the linked contract prominently once the journey reaches the contract/won
 * stage. Creating is disabled once the journey is locked (legacy gating).
 * Mutations are wired but only fire on explicit user action.
 */
export function JourneyNegotiationSection({
  customerId,
  journey,
}: {
  customerId: number;
  journey: CustomerJourney;
}) {
  const toast = useToast();
  const journeyId = journey.customer_journey_id;
  const [expanded, setExpanded] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const query = useJourneyNegotiations(journeyId);
  const negotiations = query.data?.results ?? [];
  const canCreate = !journey.customer_journey_locked;

  const onToast = (ok: boolean, message: string, detail?: string) =>
    ok ? toast.success(message) : toast.error(message, detail ? { description: detail } : undefined);

  // A journey at the contract/won stage should link its resulting contract.
  const linkedContractId = negotiations.find((n) => n.contract_id)?.contract_id;
  const atContractStage = journey.customer_journey_step === 'contract' || isWonStep(journey.customer_journey_step);

  return (
    <Card flat>
      <CardHeader
        actions={
          canCreate ? (
            <Button size="sm" variant="secondary" onClick={() => setCreating(true)}>
              Add negotiation
            </Button>
          ) : undefined
        }
      >
        <CardTitle>Deal negotiation</CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {atContractStage && linkedContractId ? (
          <Alert tone="success" title="Contract created from this journey">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
              <ExternalLink size={14} style={{ color: 'var(--vn-fg-muted)', flexShrink: 0 }} />
              <EntityLink to={`/contracts/${linkedContractId}`} id={linkedContractId} />
            </span>
          </Alert>
        ) : null}

        {query.isError && negotiations.length === 0 ? (
          <TabError title="Couldn't load negotiations" onRetry={() => query.refetch()} />
        ) : query.isLoading ? (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {[0, 1].map((i) => (
              <Skeleton key={i} style={{ height: 44 }} />
            ))}
          </div>
        ) : negotiations.length === 0 ? (
          <EmptyState
            icon={<Handshake size={28} />}
            title="No negotiations yet"
            description="Start a price negotiation to move this deal toward a contract."
          />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {negotiations.map((n) => {
              const id = n.contract_negotiation_id ?? 0;
              return (
                <NegotiationRow
                  key={id}
                  negotiation={n}
                  expanded={expanded === id}
                  onToggle={() => setExpanded((cur) => (cur === id ? null : id))}
                  onToast={onToast}
                />
              );
            })}
          </div>
        )}
      </CardBody>

      <CreateNegotiationDialog
        customerId={customerId}
        journey={journey}
        open={creating}
        onOpenChange={setCreating}
        onCreated={(ok, detail) =>
          ok ? toast.success('Negotiation created') : toast.error('Failed to create negotiation', detail ? { description: detail } : undefined)
        }
      />
    </Card>
  );
}

export default JourneyNegotiationSection;
